import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { Icon } from './Icon';
import { Button } from './Button';
import { Badge } from './Badge';
import { ConsoleModal } from './ConsoleModal';
import {
  serverControlService,
  type ServerScope, type PowerSignal, type ServerState,
} from '@/deploy/services/server-control.service';
import { POWER_META, powerDisplay, type PowerDisplay } from '@/deploy/utils/powerState';

// ============================================================
// Bloc de contrôle réutilisable pour n'importe quel serveur Ptero du panel :
//   - Badge live de l'état (En marche / Démarrage / Arrêt / Arrêté), ou « Erreur » quand le
//     CICD voit un serveur d'API running qui ne répond pas sur son ip:port (raison en infobulle) ;
//     worker sans port (contrôle de santé « process ») : infobulle dédiée sur « En marche »
//   - 4 boutons power (start / stop / restart / kill) désactivés selon l'état
//   - bouton Console (ouvre la modal ConsoleModal → WS live + files)
//
// layout="menu" (sous-lignes d'instances de la page APIs) : badge d'état et bouton « Contrôles »
// replié ; déplié, les boutons power, Console puis `menuExtra` passent dans le flux de la ligne
// parente (flex-basis 100 %), sans menu flottant qu'un conteneur overflow:hidden rognerait.
//
// Le power state est poll toutes les 3 s côté REST /state (léger, juste un
// current_state). Un refresh immédiat est déclenché juste après un power signal.
// ============================================================

export interface ServerControlsProps {
  projectId: string;
  scope: ServerScope;
  targetKey: string;
  sourceName: string;
  sourceSubtitle?: string;
  sourceAccent?: string;
  icon?: 'server' | 'zap' | 'package';
  /** Version compacte : n'affiche que start/stop + console. */
  compact?: boolean;
  /**
   * Bloque les boutons power (ex : Release en cours, qui arrête puis redémarre le serveur),
   * y compris ceux de la console. Le badge d'état continue d'être rafraîchi et la console reste
   * ouvrable pour suivre le démarrage.
   */
  disabled?: boolean;
  /** Optionnel : call après un power réussi (ex: refresh de la liste). */
  onAction?: (signal: PowerSignal) => void;
  /** "inline" (défaut) : rendu historique ; "menu" : repliable, voir l'en-tête. */
  layout?: 'inline' | 'menu';
  /** Actions ajoutées après Console dans le menu déplié (layout="menu" seulement). */
  menuExtra?: ReactNode;
}

/** État Ptero brut (boutons power) ; `unreadable` : /state en échec avant toute lecture. */
type PowerState = 'running' | 'offline' | 'starting' | 'stopping' | 'unknown' | 'unreadable';

export function ServerControls({
  projectId, scope, targetKey,
  sourceName, sourceSubtitle, sourceAccent, icon,
  compact,
  disabled,
  onAction,
  layout = 'inline',
  menuExtra,
}: ServerControlsProps) {
  const [busy, setBusy] = useState<PowerSignal | null>(null);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [power, setPower] = useState<PowerState>('unknown');
  // État affiché : celui de Ptero, ou « Erreur » (status du CICD) avec sa raison. healthCheck gardé
  // d'un relevé à l'autre (worker sans port : infobulle sur « En marche »)
  const [status, setStatus] = useState<{
    display: PowerDisplay; reason?: string; healthCheck?: ServerState['healthCheck'];
  } | null>(null);
  const [stats, setStats] = useState<{ ramMb: number; cpuPct: number; uptimeSec: number } | null>(null);
  const [errorTip, setErrorTip] = useState<string | null>(null);
  const alive = useRef(true);

  // Polling léger : refresh l'état toutes les 3 s tant que le composant est monté.
  useEffect(() => {
    alive.current = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      try {
        const s = await serverControlService.state(projectId, scope, targetKey);
        if (!alive.current) return;
        setPower(normalize(s.state));
        setStatus({ display: powerDisplay(s.status ?? s.state), reason: s.statusReason, healthCheck: s.healthCheck });
        setStats({
          ramMb: Math.round((s.memoryBytes ?? 0) / (1024 * 1024)),
          cpuPct: Math.round((s.cpuAbsolute ?? 0) * 10) / 10,
          uptimeSec: Math.round((s.uptimeMs ?? 0) / 1000),
        });
        setErrorTip(null);
      } catch (e) {
        if (!alive.current) return;
        // On ne clignote pas en rouge sur erreur transitoire ; on garde l'ancien state
        // mais on mémorise le message pour tooltip.
        setErrorTip((e as Error).message);
        setPower((p) => (p === 'unknown' ? 'unreadable' : p));
      } finally {
        if (alive.current) timer = setTimeout(tick, 3000);
      }
    };
    void tick();
    return () => {
      alive.current = false;
      if (timer) clearTimeout(timer);
    };
  }, [projectId, scope, targetKey]);

  const send = async (signal: PowerSignal) => {
    if (signal === 'kill' && !confirm('KILL force l\'arrêt sans sauvegarder. Continuer ?')) return;
    setBusy(signal);
    // Optimistic state : le badge passe en transition immédiatement
    const optimistic = signal === 'start' || signal === 'restart' ? 'starting' : signal === 'stop' ? 'stopping' : null;
    if (optimistic) {
      setPower(optimistic);
      setStatus((prev) => ({ display: optimistic, healthCheck: prev?.healthCheck }));
    }
    try {
      await serverControlService.power(projectId, scope, targetKey, signal);
      onAction?.(signal);
      // Refresh direct pour capturer starting/stopping côté Ptero
      try {
        const s = await serverControlService.state(projectId, scope, targetKey);
        setPower(normalize(s.state));
        setStatus({ display: powerDisplay(s.status ?? s.state), reason: s.statusReason, healthCheck: s.healthCheck });
      } catch { /* le polling reprendra */ }
    } catch (e) {
      alert(`${signal} KO : ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const isRunning = power === 'running' || power === 'starting';
  const isOffline = power === 'offline';
  const locked = busy !== null || !!disabled;
  // Lecture en échec : « État illisible », jamais « Erreur » (ce n'est pas un diagnostic du serveur)
  const display: PowerDisplay = power === 'unreadable' ? 'unreadable' : power === 'unknown' ? 'unknown' : status?.display ?? power;
  const meta = POWER_META[display];
  // Worker sans port : « En marche » ne dit pas qu'un port répond, seulement que le process tient
  const workerHint = display === 'running' && status?.healthCheck === 'process'
    ? 'Worker sans port : seul le process est suivi (boucle de redémarrage)' : undefined;
  const badgeTitle = display === 'error'
    ? status?.reason ?? 'Le serveur ne répond pas.'
    : display === 'unreadable' ? errorTip ?? undefined : workerHint;
  const statusBadge = (
    <span title={badgeTitle} style={{ display: 'inline-flex' }}>
      <Badge tone={meta.tone} dot>{meta.label}</Badge>
      {display === 'error' && status?.reason && <span className="sr-only"> : {status.reason}</span>}
      {workerHint && <span className="sr-only"> : {workerHint}</span>}
    </span>
  );

  // Boutons power, couleurs sémantiques : vert=Start, ambre=Restart, rouge=Stop/Kill, bleu=Console
  const powerButtons = (
    <>
      <Button variant="softSuccess" size="sm" leftIcon={<Icon name="play" size={12} />}
        disabled={locked || isRunning}
        loading={busy === 'start'}
        onClick={() => void send('start')}>Start</Button>
      {!compact && (
        <Button variant="softWarning" size="sm" leftIcon={<Icon name="refresh" size={12} />}
          disabled={locked || isOffline}
          loading={busy === 'restart'}
          onClick={() => void send('restart')}>Restart</Button>
      )}
      <Button variant="softDanger" size="sm" leftIcon={<Icon name="x" size={12} />}
        disabled={locked || isOffline}
        loading={busy === 'stop'}
        onClick={() => void send('stop')}>Stop</Button>
      {!compact && (
        <Button variant="softDanger" size="sm"
          disabled={locked || isOffline}
          loading={busy === 'kill'}
          onClick={() => void send('kill')} title="Kill (force)">
          Kill
        </Button>
      )}
      <Button variant="primary" size="sm" leftIcon={<Icon name="git-commit" size={12} />}
        onClick={() => setConsoleOpen(true)}>Console</Button>
    </>
  );

  const consoleModal = consoleOpen && (
    <ConsoleModal
      open
      onClose={() => setConsoleOpen(false)}
      projectId={projectId}
      scope={scope}
      targetKey={targetKey}
      sourceName={sourceName}
      sourceSubtitle={sourceSubtitle}
      sourceAccent={sourceAccent}
      icon={icon}
      powerDisabled={disabled}
    />
  );

  if (layout === 'menu') {
    // Échap referme et rend le focus au bouton, depuis le bouton comme depuis le menu.
    const closeOnEscape = (e: ReactKeyboardEvent) => {
      if (e.key !== 'Escape' || !menuOpen) return;
      e.stopPropagation();
      setMenuOpen(false);
      toggleRef.current?.focus();
    };
    return (
      <>
        <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }} title={errorTip ?? undefined}>
          {statusBadge}
          <Button ref={toggleRef} variant="ghost" size="sm"
            rightIcon={<Icon name="chevron-down" size={11}
              style={{ transform: menuOpen ? 'rotate(180deg)' : undefined, transition: 'transform 120ms' }} />}
            aria-expanded={menuOpen} aria-controls={menuId} aria-label={`Contrôles de ${sourceName}`}
            onClick={() => setMenuOpen((o) => !o)} onKeyDown={closeOnEscape}>
            Contrôles
          </Button>
        </div>
        {menuOpen && (
          <div id={menuId} role="group" aria-label={`Contrôles de ${sourceName}`} onKeyDown={closeOnEscape}
            style={{
              flexBasis: '100%', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
              padding: '6px 8px', borderRadius: 8,
              background: 'var(--color-background-secondary)', border: '1px solid var(--color-border)',
            }}>
            {powerButtons}
            {menuExtra}
          </div>
        )}
        {consoleModal}
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'inline-flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
        title={errorTip ?? undefined}>
        {/* Colonne STATUT + PERF : largeur réservée + cellules de chiffres à largeur fixe
            (tabular-nums, alignées à droite) → les valeurs qui changent toutes les 3 s ne
            poussent JAMAIS les boutons (fini le jitter), et les rangées restent alignées. */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          flex: '0 0 auto',
        }}>
          {statusBadge}
          {isRunning && stats && stats.uptimeSec > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 11, color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-family-mono)', fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }} title={`Uptime ${fmtUptime(stats.uptimeSec)} · RAM ${stats.ramMb} MB · CPU ${stats.cpuPct}%`}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <Icon name="clock" size={10} />
                <span style={{ display: 'inline-block', width: 48, textAlign: 'right' }}>{fmtUptime(stats.uptimeSec)}</span>
              </span>
              <span style={{ opacity: 0.4 }}>·</span>
              <span style={{ display: 'inline-block', width: 58, textAlign: 'right' }}>{stats.ramMb} MB</span>
              <span style={{ opacity: 0.4 }}>·</span>
              <span style={{ display: 'inline-block', width: 46, textAlign: 'right' }}>{stats.cpuPct}%</span>
            </span>
          )}
        </div>

        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {powerButtons}
        </div>
      </div>

      {consoleModal}
    </>
  );
}

function normalize(s: string): PowerState {
  switch (s?.toLowerCase()) {
    case 'running': return 'running';
    case 'offline': return 'offline';
    case 'starting': return 'starting';
    case 'stopping': return 'stopping';
    default: return 'unknown';
  }
}

function fmtUptime(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h${Math.floor((sec % 3600) / 60)}m`;
  return `${Math.floor(sec / 86400)}j${Math.floor((sec % 86400) / 3600)}h`;
}
